import React from "react";
import ColorDisclaimer from "./ColorDisclaimer";
import IndicationDisclaimer from "./commonComps/3DEmblem/IndicationDisclaimer";
import { Step } from "../../types/wizard";
import { Steps } from "../../constants";
import SummaryContent from "../Wizard/commonComps/SummaryContent";
import { useEffect, useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import { wizardStore } from "../../stores/wizard";
import "./QuickView.css";

type QuickViewLayoutProps = {
  columns: { column: number; step: Step; content: JSX.Element }[];
  screenSize: string;
};

export default function QuickViewLayout({ columns, screenSize }: QuickViewLayoutProps) {
  const wizSnap = useSnapshot(wizardStore);
  const [activeStep, setActiveStep] = useState<Step | null>(null);

  useEffect(() => {
    if (wizSnap.step)
      setActiveStep(wizSnap.step as Step);
  }, [wizSnap]);

  const activeColumns = useMemo(
    () => [1, 2, 3, 4].filter((col) => columns.some((item) => item.column === col)),
    [columns]
  );

  const columnWidth = useMemo(() => {
    if (screenSize === "small") return "w-full";

    switch (activeColumns.length) {
      case 2:
        return "w-1/2";
      case 3:
      case 1:
        return "w-1/3";
      default:
        return "w-1/4";
    }
  }, [activeColumns.length, screenSize]);

  const lastColumnTitle =
    columns.find((item) => item.column === 4)?.content?.props.step?.title || "";

  return (
    <React.Fragment>
      <div className="relative h-full">
        {activeStep?.id === Steps.RequestDetails ? (
          <div className="flex gap-2 items-start justify-start">
            <div className="flex flex-col items-center justify-start relative text-left ml-4 mr-4 mb-4 w-full">
              <SummaryContent />
            </div>
          </div>
        ) : (
          <div className="flex gap-2 items-start justify-start">
            {activeColumns.map((col) => {
              const columnData = columns.find((item) => item.column === col);
              const step = columnData?.step;

              return (
                <div key={col} className={`flex flex-col items-center justify-start relative text-left gap-5 ml-4 mr-4 ${columnWidth}`}>
                  <div className="w-full mb-4">
                    {col === 1 && <h4 className="font-bold text-lg my-4">PRODUCT DETAIL</h4>}
                    {col === 2 && <h4 className="font-bold text-lg my-4">DESIGN</h4>}
                    {col === 3 && (
                      <>
                        <h4 className="font-bold text-lg my-4">COLOURS</h4>
                        <ColorDisclaimer step={step} title="Let Stahls Choose My Colours" />
                      </>
                    )}
                    {col === 4 && (
                      <>
                        <h4 className="font-bold text-lg my-4 uppercase">{lastColumnTitle}</h4>
                        <IndicationDisclaimer step={step} title="Let Stahls Choose My Indications" />
                      </>
                    )}
                    <hr className="dashed-title" />
                  </div>

                  {columns
                    .filter((item) => item.column === col)
                    .map((item, index) => (
                      <div key={index} className="w-full">
                        {screenSize !== "default" &&
                          [Steps.ThreadColors, Steps.HolographicColors].includes(item.step.id) && (
                            <>
                              <h4 className="font-bold text-lg my-4">COLOURS</h4>
                              <ColorDisclaimer step={item.step} title="Let Stahls Choose My Colours" />
                            </>
                          )}
                        {screenSize !== "default" &&
                          [Steps.Emblem3d, Steps.EmblemPrintStitch, Steps.EmblemLeather].includes(item.step.id) && (
                            <>
                              <h4 className="font-bold text-lg my-4 uppercase">{item.step.title}</h4>
                              <IndicationDisclaimer step={item.step} title="Let Stahls Choose My Indications" />
                            </>
                          )}
                        {item.content}
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </React.Fragment>
  );
}